import { Cpu, Truck as TruckIcon, Users, Lock } from "lucide-react";
import PageHeader from "../components/pageHeader";
import { useTranslation } from "../../../i18n/useTranslation";

const STEPS = [
  { n: "01", title: "Scope", body: "We understand your devices, environments, and goals." },
  { n: "02", title: "Source", body: "Devices and services are matched to specialist partners in our network." },
  { n: "03", title: "Deploy & Manage", body: "Rollout, monitoring, and support run through one platform." },
  { n: "04", title: "Report", body: "You see fleet health and activity in one place, continuously." },
];

const HowWeWork = () => {
  const { t } = useTranslation();
  const h = t("deviceworks.pageHeaders.howWeWork");
  return (
    <div>
      <PageHeader eyebrow={h.eyebrow} title={h.title} description={h.description} />

      {/* 4-step */}
      <div className="max-w-5xl mx-auto px-5 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="dw-card">
              <span className="dw-mono text-sm" style={{ color: "var(--dw-accent)" }}>{s.n}</span>
              <h3 className="pt-2 text-base">{s.title}</h3>
              <p className="dw-mini pt-1 text-sm">{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* two supply chains */}
      <div className="dw-panel-bg border-y py-16 px-5" style={{ borderColor: "var(--dw-line)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="dw-eyebrow mb-2">Two Supply Chains</div>
            <h2>Digital and physical, working in sync</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="dw-card">
              <Cpu size={28} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
              <h3 className="pt-3">Digital — DeviceWorks-owned</h3>
              <p className="dw-mini pt-1 text-sm">
                The platform, the analytics, the single dashboard — built and owned by
                DeviceWorks, tying every device and every partner interaction together.
              </p>
            </div>
            <div className="dw-card">
              <TruckIcon size={28} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
              <h3 className="pt-3">Physical — partner-executed</h3>
              <p className="dw-mini pt-1 text-sm">
                Sourcing, field repair, logistics, and kitting are carried out by vetted
                specialist partners — coordinated by us, executed by experts in each area.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* how it comes together */}
      <div className="max-w-4xl mx-auto px-5 py-16 text-center">
        <Users className="mx-auto" size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
        <h2 className="pt-3">How it all comes together</h2>
        <p className="dw-mini pt-4">
          Clients tell us what they need. Field support partners execute the physical
          work — repairs, deployment, kitting — on the ground. Logistics partners move
          devices where they need to go. DeviceWorks sits at the center, coordinating all
          three and giving clients one place to see the whole picture.
        </p>
      </div>

      {/* data privacy & security */}
      <div className="dw-panel-bg border-t py-16 px-5" style={{ borderColor: "var(--dw-line)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Lock className="mx-auto" size={32} strokeWidth={1.5} style={{ color: "var(--dw-accent)" }} />
          <h2 className="pt-3">Data Privacy &amp; Security</h2>
          <p className="dw-mini pt-4">
            Every device that passes through our network — whether it's being deployed,
            repaired, or retired — follows chain-of-custody and data-handling standards.
            Access to fleet data is controlled and auditable, and every delivery partner
            is required to meet the same data-handling bar before doing any work.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HowWeWork;
